/**
 * Task media viewer component for displaying screenshots, recordings, and GIFs
 */

import { useState } from 'react'
import { Download, ExternalLink, ZoomIn } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import type { TaskMedia } from '../../types/anchor-browser'

interface TaskMediaViewerProps {
  media?: TaskMedia
  isLoading?: boolean
  error?: Error | null
  taskStatus?: string
}

interface MediaItemProps {
  src: string
  alt: string
  type: 'image' | 'video' | 'gif'
  index?: number
}

function MediaItem({ src, alt, type, index }: MediaItemProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)

  const handleDownload = async () => {
    try {
      const response = await fetch(src)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${alt.replace(/\s+/g, '_')}.${type === 'gif' ? 'gif' : type === 'video' ? 'mp4' : 'png'}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download media:', error)
    }
  }

  if (type === 'video') {
    return (
      <div className="relative group">
        <video
          src={src}
          controls
          className="w-full rounded border hover:shadow-lg transition-shadow"
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => window.open(src, '_blank')}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleDownload}
          >
            <Download className="h-3 w-3" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative group">
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogTrigger asChild>
          <img
            src={src}
            alt={alt}
            className="w-full rounded border hover:shadow-lg transition-shadow cursor-pointer"
          />
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{alt}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setIsFullscreen(true)}
        >
          <ZoomIn className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => window.open(src, '_blank')}
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleDownload}
        >
          <Download className="h-3 w-3" />
        </Button>
      </div>
      
      {index !== undefined && (
        <div className="absolute bottom-2 left-2">
          <Badge variant="secondary" className="text-xs">
            {index + 1}
          </Badge>
        </div>
      )}
    </div>
  )
}

export function TaskMediaViewer({ media, isLoading, error, taskStatus }: TaskMediaViewerProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Media & Screenshots</CardTitle>
          <CardDescription>Visual recordings and screenshots from task execution</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm text-muted-foreground">Loading media...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Media & Screenshots</CardTitle>
          <CardDescription>Visual recordings and screenshots from task execution</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Failed to load media files. This might be normal for tasks that haven't generated screenshots yet.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  const hasMedia = media && (
    (media.screenshots && media.screenshots.length > 0) ||
    (media.recordings && media.recordings.length > 0) ||
    media.gif
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Media & Screenshots</span>
          {hasMedia && (
            <div className="flex gap-2">
              {media.screenshots && media.screenshots.length > 0 && (
                <Badge variant="outline">{media.screenshots.length} screenshots</Badge>
              )}
              {media.recordings && media.recordings.length > 0 && (
                <Badge variant="outline">{media.recordings.length} recordings</Badge>
              )}
              {media.gif && (
                <Badge variant="outline">GIF available</Badge>
              )}
            </div>
          )}
        </CardTitle>
        <CardDescription>Visual recordings and screenshots from task execution</CardDescription>
      </CardHeader>
      <CardContent>
        {hasMedia ? (
          <div className="space-y-6">
            {/* Screenshots */}
            {media.screenshots && media.screenshots.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  Screenshots
                  <Badge variant="secondary">{media.screenshots.length}</Badge>
                </h4>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {media.screenshots.map((screenshot, index) => (
                    <MediaItem
                      key={index}
                      src={typeof screenshot === 'string' ? screenshot : screenshot.url}
                      alt={`Screenshot ${index + 1}`}
                      type="image"
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recordings */}
            {media.recordings && media.recordings.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  Video Recordings
                  <Badge variant="secondary">{media.recordings.length}</Badge>
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {media.recordings.map((recording, index) => (
                    <MediaItem
                      key={index}
                      src={typeof recording === 'string' ? recording : recording.url}
                      alt={`Recording ${index + 1}`}
                      type="video"
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* GIF Recording */}
            {media.gif && (
              <div>
                <h4 className="text-sm font-medium mb-3">Task Recording (GIF)</h4>
                <div className="max-w-2xl">
                  <MediaItem
                    src={typeof media.gif === 'string' ? media.gif : media.gif.url}
                    alt="Task execution recording"
                    type="gif"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {taskStatus === 'running'
                ? 'Waiting for media to be generated...'
                : taskStatus === 'created'
                ? 'Task hasn\'t started yet. Media will be available once execution begins.'
                : taskStatus === 'finished'
                ? 'Task completed but no media files were captured.'
                : 'No media files available yet.'
              }
            </p>
            {(taskStatus === 'running' || taskStatus === 'finished') && (
              <p className="text-xs text-muted-foreground mt-2">
                Media files may take a moment to appear after task execution.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
